import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Info, RotateCcw, Search } from 'lucide-react';
import {
  Button,
  Card,
  CardBody,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Progress,
  SegmentedControl,
  Spinner,
  Stepper,
  Switch,
  Tooltip,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { DataTable, Pagination, StatusPill, type ColumnDef, type StatusTone } from '@vnetwork/ui/data';
import { Alert, ConfirmDialog } from '@vnetwork/ui/feedback';
import {
  useReconciliationCycle,
  useSendReconciliationEmail,
  reconciliationKeys,
  type ServiceItem,
} from '@/api/reconciliation';
import { TableLink } from '@/components/table-link';
import { useMe } from '@/api/me';
import { formatMoney } from '@/lib/format';
import {
  useEnqueueSignJobs,
  useSignJobs,
  useRetrySignJob,
  useCancelSignJob,
  useOpenSignedFile,
  type SignJob,
  type SignJobStep,
} from '@/api/sign-jobs';
import {
  apiErrorMessage,
  isSendBatchActive,
  isSendBatchDispatching,
  reconJobKeys,
  useApproveInvoices,
  useEnqueueReconJobs,
  useLatestSendBatch,
  useReconJobs,
  useRetryFailedSend,
  useRetryReconJob,
  type ReconJob,
  type ReconJobStep,
  type SendBatch,
  type SendBatchItem,
} from '@/api/reconciliation-jobs';
import type { DocumentType } from '@/api/esign-documents';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';

/* ── Statement pipeline, managed as one progress-tracked control panel ────────
 * Every stage is backed by PERSISTED server state, so a page refresh shows the
 * same progress, per-item step / error and retry:
 *   1. Sinh  — POST /reconciliation/jobs {kind:generate, service_ids} → one job per
 *              service (queued→creating→syncing_bi→done), polled via GET /jobs
 *   2. Gộp   — POST /reconciliation/jobs {kind:merge, invoice_ids} → one job
 *              (queued→merging→syncing_bi→done)
 *   3. Xác nhận — POST /reconciliation/invoices/approve {invoice_ids}: waiting-confirm →
 *              approved. State is the invoice's own status; an unconfirmed invoice
 *              cannot be signed
 *   4. Ký số — POST /reconciliation/sign-jobs, one job per (invoice, document). ĐNTT
 *              and BBĐS are managed as two separate groups: each document is signed,
 *              retried and re-signed (force) on its own
 *   5. Gửi   — send-email(scope) → latest batch via send-status (per-item status);
 *              failed items re-sent via send-batches/:id/retry-failed
 * Every mutating action is behind a ConfirmDialog (USER triggers live mutations).
 * ────────────────────────────────────────────────────────────────────────── */

type StageStatus = 'idle' | 'running' | 'done' | 'partial' | 'error';

const PROGRESS_TONE: Record<StageStatus, 'primary' | 'success' | 'warning' | 'danger' | 'neutral'> = {
  idle: 'neutral',
  running: 'primary',
  done: 'success',
  partial: 'warning',
  error: 'danger',
};
const PILL_TONE: Record<StageStatus, StatusTone> = {
  idle: 'neutral',
  running: 'info',
  done: 'success',
  partial: 'warning',
  error: 'danger',
};
const STATUS_LABEL: Record<StageStatus, string> = {
  idle: 'Chưa chạy',
  running: 'Đang chạy',
  done: 'Hoàn tất',
  partial: 'Có lỗi',
  error: 'Thất bại',
};

/* ── Step → human label/tone ──────────────────────────────────────────────── */
const SIGN_STEP_LABEL: Record<SignJobStep, string> = {
  queued: 'Chờ xử lý',
  rendering: 'Đang kết xuất',
  submitting: 'Đang gửi MISA',
  signing: 'Đang ký',
  storing: 'Đang lưu',
  done: 'Hoàn tất',
  failed: 'Thất bại',
};
const JOB_STEP_LABEL: Record<ReconJobStep, string> = {
  queued: 'Chờ xử lý',
  creating: 'Đang tạo hoá đơn',
  merging: 'Đang gộp',
  syncing_bi: 'Đang đồng bộ BI',
  unmerging: 'Đang tách gộp',
  rolling_back: 'Đang hoàn tác',
  done: 'Hoàn tất',
};
const SEND_ITEM_LABEL: Record<string, string> = {
  pending: 'Chờ gửi',
  dispatching: 'Đang chuyển cho automation',
  queued: 'Chờ automation gửi',
  success: 'Đã gửi',
  failed: 'Lỗi',
  skipped: 'Bỏ qua',
};
/** Why the send worker skipped / failed an invoice, in operator words. */
const SEND_REASON_LABEL: Record<string, string> = {
  'already-sent': 'Đã gửi trước đó — bỏ qua để không gửi trùng',
  'no-sendable-mailer': 'Khách chưa có mailer gửi được (customer_mailers) — cấu hình mailer hoặc gửi tới email khách',
  'automation-delivery-timeout': 'Automation không báo kết quả sau 30 phút — kiểm tra hàng đợi gửi mail rồi gửi lại',
  'automation-delivery-failed': 'Automation gửi thất bại nhưng không ghi lý do',
  'documents-not-signed': 'BBĐS và ĐNTT chưa ký — ký ở bước 4 rồi gửi lại',
  'statement-not-signed': 'BBĐS chưa ký — ký ở bước 4 rồi gửi lại',
  'payment-request-not-signed': 'ĐNTT chưa ký — ký ở bước 4 rồi gửi lại',
};
const sendReason = (r?: string | null) => (r ? SEND_REASON_LABEL[r] ?? r : null);
/** A skipped item is only fine when it was already sent; any other skip needs a human. */
const isSendProblem = (it: SendBatchItem) => it.status === 'failed' || (it.status === 'skipped' && it.reason !== 'already-sent');

const SEND_ITEM_TONE: Record<string, StatusTone> = {
  pending: 'info',
  dispatching: 'info',
  queued: 'info',
  success: 'success',
  failed: 'danger',
  skipped: 'neutral',
};
/** Handed over (or about to be) but without a real delivery result yet. */
const isSendInFlight = (status: string) => ['pending', 'dispatching', 'queued'].includes(status);
const SIGN_DOC_TYPES: DocumentType[] = ['payment-request', 'reconciliation-statement'];
const DOC_LABEL: Record<DocumentType, string> = {
  'payment-request': 'ĐNTT',
  'reconciliation-statement': 'BBĐS',
};
const DOC_TITLE: Record<DocumentType, string> = {
  'payment-request': 'Đề nghị thanh toán (ĐNTT)',
  'reconciliation-statement': 'Biên bản đối soát (BBĐS)',
};

const isSignFailed = (j: SignJob) => j.status === 'failed' || j.step === 'failed';
function signJobTone(j: SignJob): StatusTone {
  if (j.status === 'done') return 'success';
  if (isSignFailed(j)) return 'danger';
  return j.step_error ? 'warning' : 'info';
}
function signJobLabel(j: SignJob): string {
  if (j.status === 'done') return 'Đã ký';
  if (isSignFailed(j)) return 'Ký lỗi';
  const step = SIGN_STEP_LABEL[j.step] ?? j.step;
  return j.step_error ? `Tự thử lại · ${step}` : step;
}

/** Row states of a sign group, in display order: what is moving first, untouched next, finished last. */
const SIGN_STATES = [
  { value: 'processing', label: 'Đang ký' },
  { value: 'failed', label: 'Lỗi' },
  { value: 'unsigned', label: 'Chưa ký' },
  { value: 'signed', label: 'Đã ký' },
];

const GEN_STATES = [
  { value: 'processing', label: 'Đang chạy' },
  { value: 'failed', label: 'Lỗi' },
  { value: 'pending', label: 'Chờ sinh' },
  { value: 'done', label: 'Đã sinh' },
];
const MERGE_STATES = [
  { value: 'processing', label: 'Đang chạy' },
  { value: 'failed', label: 'Lỗi' },
  { value: 'done', label: 'Đã gộp' },
];
const APPROVE_STATES = [
  { value: 'failed', label: 'Lỗi' },
  { value: 'waiting', label: 'Chờ xác nhận' },
  { value: 'approved', label: 'Đã xác nhận' },
];
/** Why an invoice could not be confirmed, in operator words. */
const APPROVE_REASON_LABEL: Record<string, string> = {
  'invoice-access-denied': 'Bạn không có quyền trên hoá đơn của khách hàng này',
  'invoice-not-found': 'Không tìm thấy hoá đơn',
  'invoice-approve-failed': 'Xác nhận thất bại — thử lại, nếu vẫn lỗi thì xác nhận ở trang hoá đơn',
};
const SEND_STATES = [
  { value: 'sending', label: 'Chờ kết quả gửi' },
  { value: 'failed', label: 'Lỗi' },
  { value: 'skipped', label: 'Bỏ qua' },
  { value: 'unsent', label: 'Chưa gửi' },
  { value: 'sent', label: 'Đã gửi' },
];
/** "Đang chạy 1 · Lỗi 0 · Chờ sinh 5 · Đã sinh 10" — every item accounted for by state. */
const stateSummary = (items: ListItem[], states: { value: string; label: string }[]) =>
  states.map((st) => `${st.label} ${items.filter((i) => i.state === st.value).length}`).join(' · ');

/** Sign state of ONE document type across the cohort's invoices. */
interface SignGroup {
  docType: DocumentType;
  /** Latest live job per invoice. */
  latest: Map<number, SignJob>;
  /** Signed copy in effect per invoice — may be older than a re-sign still in flight or failed. */
  signed: Map<number, SignJob>;
  /** Invoices whose document was never put in the sign queue. */
  unsignedIds: number[];
  failed: SignJob[];
  processing: number;
  status: StageStatus;
}

/** `jobs` must be newest first. Superseded jobs are history and never count. */
function buildSignGroup(docType: DocumentType, invoiceIds: number[], jobs: SignJob[]): SignGroup {
  const latest = new Map<number, SignJob>();
  const signed = new Map<number, SignJob>();
  for (const j of jobs) {
    if (j.document_type !== docType || j.status === 'superseded') continue;
    if (!latest.has(j.invoice_id)) latest.set(j.invoice_id, j);
    if (j.status === 'done' && j.signed_file_key && !signed.has(j.invoice_id)) signed.set(j.invoice_id, j);
  }
  const live = Array.from(latest.values());
  const failed = live.filter(isSignFailed);
  const processing = live.filter((j) => j.status === 'processing').length;
  const status: StageStatus =
    processing > 0
      ? 'running'
      : failed.length > 0
        ? 'partial'
        : invoiceIds.length > 0 && invoiceIds.every((id) => signed.has(id))
          ? 'done'
          : 'idle';
  return { docType, latest, signed, unsignedIds: invoiceIds.filter((id) => !latest.has(id)), failed, processing, status };
}
function reconJobTone(j: ReconJob): StatusTone {
  if (j.status === 'done') return 'success';
  if (j.status === 'failed') return 'danger';
  return j.step_error ? 'warning' : 'info';
}
const jobStepLabel = (step: string) => JOB_STEP_LABEL[step as ReconJobStep] ?? step;
const mergeJobTitle = (j: ReconJob) => {
  const ids = j.input?.invoice_ids;
  return Array.isArray(ids) ? `Gộp ${ids.length} hoá đơn` : `Tác vụ gộp #${j.id}`;
};

/** Stage status from a set of persisted jobs (latest per target). */
function jobsStageStatus(jobs: { status: string }[], failedCount: number, busy: boolean): StageStatus {
  if (busy || jobs.some((j) => j.status === 'processing')) return 'running';
  if (jobs.length === 0) return 'idle';
  if (failedCount === jobs.length) return 'error';
  return failedCount > 0 ? 'partial' : 'done';
}

/** Stage status from the latest email batch; null = no batch yet (fall back to cycle counts). */
function sendStageStatus(b: SendBatch | null, busy: boolean): StageStatus | null {
  if (busy || isSendBatchActive(b)) return 'running';
  // A finished test batch reached no customer: it says nothing about the stage.
  if (!b || b.test_recipient) return null;
  if (b.status === 'scheduled') return 'running';
  const problems = (b.items ?? []).filter(isSendProblem).length || b.failed;
  if (problems > 0) return b.sent === 0 && problems === b.total ? 'error' : 'partial';
  return 'done';
}

/** Failed first, then in-flight, then done — the operator looks at problems first. */
const JOB_ORDER: Record<string, number> = { failed: 0, processing: 1, done: 2 };
const byAttention = (a: ReconJob, b: ReconJob) =>
  (JOB_ORDER[a.status] ?? 3) - (JOB_ORDER[b.status] ?? 3) || a.id - b.id;

/** Runs `onAdvance` whenever `count` grows after the first observed value. */
function useOnIncrease(count: number, onAdvance: () => void) {
  const prev = useRef<number | null>(null);
  const cb = useRef(onAdvance);
  cb.current = onAdvance;
  useEffect(() => {
    if (prev.current !== null && count > prev.current) cb.current();
    prev.current = count;
  }, [count]);
}

type ConfirmState =
  | { action: 'generate' | 'merge' | 'send' | 'send-retry' }
  | { action: 'approve'; ids: number[] }
  | { action: 'sign'; docType: DocumentType; ids: number[] }
  | { action: 'resign'; docType: DocumentType; invoiceId: number }
  | { action: 'job-retry'; ids: number[] }
  | { action: 'generate-one'; service: ServiceItem }
  | { action: 'rollback' | 'regenerate'; service: ServiceItem }
  | { action: 'sign-job-retry'; ids: number[] }
  | { action: 'sign-job-cancel'; job: SignJob }
  | { action: 'send-force'; invoiceIds: number[]; mode: 'resend' | 'no-mailer' | 'after-sign' };
const RETRY_ACTIONS: ConfirmState['action'][] = ['job-retry', 'sign-job-retry', 'send-retry'];

/** One line of a stage's item list. */
interface ListItem {
  key: string;
  title: ReactNode;
  /** Plain text matched by the search box. */
  text: string;
  sub?: ReactNode;
  /** Money column (shown when any row has one). */
  amount?: ReactNode;
  /** Optional extra column (heading from ItemList `headings.extra`). */
  extra?: ReactNode;
  status: ReactNode;
  /** Why it failed / is retrying — shown under the line, full text on hover. */
  error?: string | null;
  /** Still needs the operator (failed, auto-retrying, not yet done): listed first and shown by the "Cần xử lý" filter. */
  attention?: boolean;
  /** Bucket for a list filtered by state (see ItemList `states`). */
  state?: string;
  action?: ReactNode;
}

const LIST_PAGE_SIZE = 20;

interface ListHeadings {
  title: string;
  sub?: string;
  extra?: string;
}

const cellClip = { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;

/**
 * Per-item table used by every stage card: column headings, 20 rows per page, a
 * filter per state with counts and a search box. Rows are ordered by state (what is
 * moving first, finished last) and the error sits right under the row's status.
 */
function ItemList({
  items,
  unit,
  states,
  headings,
}: {
  items: ListItem[];
  unit: string;
  states?: { value: string; label: string }[];
  headings: ListHeadings;
}) {
  const attention = items.filter((i) => i.attention).length;
  const [view, setView] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  if (items.length === 0) return null;

  // A state list opens on everything, so an item never drops out of sight when its
  // state changes (e.g. "Chưa ký" → "Đang ký"); rows are ordered by state instead.
  const effView = view ?? (states ? 'all' : attention > 0 && items.length > 8 ? 'attention' : 'all');
  const stateRank = (i: ListItem) => (states ? states.findIndex((st) => st.value === i.state) : Number(!i.attention));
  const needle = q.trim().toLowerCase();
  const shown = [...items]
    .sort((a, b) => stateRank(a) - stateRank(b))
    .filter((i) => (effView === 'all' ? true : states ? i.state === effView : i.attention))
    .filter((i) => !needle || i.text.toLowerCase().includes(needle));
  const filterItems = states
    ? [
        { value: 'all', label: `Tất cả ${items.length}` },
        ...states.map((st) => ({ value: st.value, label: `${st.label} ${items.filter((i) => i.state === st.value).length}` })),
      ]
    : [
        { value: 'attention', label: `Cần xử lý ${attention}` },
        { value: 'all', label: `Tất cả ${items.length}` },
      ];
  const withTools = items.length > 8;

  const pageCount = Math.max(1, Math.ceil(shown.length / LIST_PAGE_SIZE));
  const curPage = Math.min(page, pageCount);
  const from = (curPage - 1) * LIST_PAGE_SIZE;
  const rows = shown.slice(from, from + LIST_PAGE_SIZE);

  const columns: ColumnDef<ListItem, unknown>[] = [
    {
      id: 'title',
      header: headings.title,
      cell: ({ row }) => <span style={{ ...cellClip, maxWidth: 360, fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.title}</span>,
    },
    ...(headings.sub
      ? [
          {
            id: 'sub',
            header: headings.sub,
            cell: ({ row }) => (
              <span title={typeof row.original.sub === 'string' ? row.original.sub : undefined} style={{ ...cellClip, ...mutedSm, maxWidth: 420 }}>
                {row.original.sub}
              </span>
            ),
          } satisfies ColumnDef<ListItem, unknown>,
        ]
      : []),
    ...(items.some((i) => i.amount != null)
      ? [
          {
            id: 'amount',
            header: () => <span style={{ display: 'block', textAlign: 'right' }}>Số tiền</span>,
            cell: ({ row }) => <span style={{ display: 'block', textAlign: 'right', whiteSpace: 'nowrap' }}>{row.original.amount}</span>,
          } satisfies ColumnDef<ListItem, unknown>,
        ]
      : []),
    ...(headings.extra
      ? [{ id: 'extra', header: headings.extra, cell: ({ row }) => row.original.extra } satisfies ColumnDef<ListItem, unknown>]
      : []),
    {
      id: 'status',
      header: 'Trạng thái',
      cell: ({ row }) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          {row.original.status}
          {/* The error stays out of the row: hover (or focus) the icon to read it in full. */}
          {row.original.error && (
            <Tooltip content={<span style={{ display: 'block', maxWidth: 420, whiteSpace: 'normal', wordBreak: 'break-word' }}>{row.original.error}</span>}>
              <span
                tabIndex={0}
                aria-label={`Lỗi: ${row.original.error}`}
                style={{ display: 'inline-flex', color: 'var(--vn-danger-fg, var(--vn-fg-muted))', cursor: 'help' }}
              >
                <Info size={15} />
              </span>
            </Tooltip>
          )}
        </div>
      ),
    },
    ...(items.some((i) => i.action)
      ? [
          {
            id: 'action',
            header: () => <span style={{ display: 'block', textAlign: 'right' }}>Thao tác</span>,
            // Negative block margin: a button is taller than a line of text, so rows with an
            // action would otherwise be taller than rows without one.
            cell: ({ row }) => <div style={{ display: 'flex', justifyContent: 'flex-end', whiteSpace: 'nowrap', marginBlock: -6 }}>{row.original.action}</div>,
          } satisfies ColumnDef<ListItem, unknown>,
        ]
      : []),
  ];

  return (
    <div style={{ marginTop: 'var(--vn-space-3)' }}>
      {withTools && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--vn-space-2)' }}>
          <SegmentedControl
            size="sm"
            aria-label={`Lọc ${unit}`}
            value={effView}
            onValueChange={(v) => {
              setView(v);
              setPage(1);
            }}
            items={filterItems}
          />
          <Input
            inputSize="sm"
            placeholder={`Tìm ${unit}…`}
            aria-label={`Tìm ${unit}`}
            startSlot={<Search size={14} />}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            style={{ width: 240 }}
          />
        </div>
      )}
      {/* width 0 + minWidth 100%: cells are single-line, so without this the widest row
          becomes the page's minimum width and pushes the layout off-screen. The table
          takes the card's width and scrolls sideways itself if a row cannot fit. */}
      <div style={{ width: 0, minWidth: '100%', overflowX: 'auto' }}>
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => r.key}
          enableSorting={false}
          emptyState={
            <div style={{ ...mutedXs, padding: 'var(--vn-space-3)' }}>
              {effView === 'attention' && !needle ? 'Không có mục nào cần xử lý.' : 'Không có mục nào ở mục này.'}
            </div>
          }
          footer={
            shown.length > LIST_PAGE_SIZE ? (
              <Pagination
                page={curPage}
                pageCount={pageCount}
                onPageChange={setPage}
                summary={`Hiển thị ${from + 1}–${from + rows.length} / ${shown.length} ${unit}`}
              />
            ) : (
              <span style={mutedXs}>{`${shown.length} ${unit}`}</span>
            )
          }
        />
      </div>
    </div>
  );
}

const mutedSm = { fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' } as const;
const mutedXs = { fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' } as const;

export function ReconciliationPipelineTab({ cohortKey }: { cohortKey: string }) {
  const queryClient = useQueryClient();
  const cycleQuery = useReconciliationCycle(cohortKey);

  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  // Test sends default to the operator's own address; typing replaces it.
  const me = useMe().data;
  const [testEmailInput, setTestEmail] = useState<string | null>(null);
  const testEmail = testEmailInput ?? me?.employee?.employee_email_company ?? me?.employee?.employee_email ?? '';
  // Invoice whose "Gửi thử" dialog is open.
  const [testInvoiceId, setTestInvoiceId] = useState<number | null>(null);
  // Ký số / Gửi email skip 0-đồng invoices by default; the operator can turn the filter off.
  const [skipZero, setSkipZero] = useState(true);

  const enqueueJobs = useEnqueueReconJobs();
  const retryJobs = useRetryReconJob();
  const approveInvoices = useApproveInvoices();
  const enqueueSign = useEnqueueSignJobs();
  const retrySignJob = useRetrySignJob();
  const cancelSignJob = useCancelSignJob();
  const openSignedFile = useOpenSignedFile();
  const sendEmail = useSendReconciliationEmail(cohortKey);
  const retryFailedSend = useRetryFailedSend(cohortKey);

  const data = cycleQuery.data;
  const { pendingServiceIds, invoiceIds, zeroIds, unapprovedIds, mergedParents, invoiceCodes } = useMemo(() => {
    const services = data?.services ?? [];
    const invoices = data?.invoices ?? [];
    return {
      zeroIds: new Set(invoices.filter((i) => Number(i.invoice_total) === 0).map((i) => i.invoice_id)),
      pendingServiceIds: services
        .filter((s) => s.status === 'pending')
        .map((s) => Number(s.service_id))
        .filter(Number.isFinite),
      invoiceIds: invoices.map((i) => i.invoice_id),
      // Not confirmed yet: cannot be signed (the server refuses too).
      unapprovedIds: invoices.filter((i) => i.invoice_status === 'waiting-confirm').map((i) => i.invoice_id),
      mergedParents: invoices.filter((i) => (i.service_count ?? 1) > 1).length,
      invoiceCodes: new Map(invoices.map((i) => [i.invoice_id, i.invoice_code])),
    };
  }, [data]);

  /* ── Persisted state (each query polls while its items are in flight) ───── */
  const jobsQuery = useReconJobs(cohortKey, { poll: true });
  const jobs = jobsQuery.data ?? [];
  // Superseded jobs (their invoice was cancelled / un-merged since) are history, not progress.
  const liveJobs = jobs.filter((j) => j.status !== 'superseded');
  const SERVICE_KINDS = ['generate', 'regenerate', 'rollback'];
  const genJobs = liveJobs.filter((j) => SERVICE_KINDS.includes(j.kind)).sort(byAttention);
  const mergeJobs = liveJobs.filter((j) => j.kind === 'merge').sort(byAttention);
  const mergeSuperseded = jobs.some((j) => j.kind === 'merge' && j.status === 'superseded');
  const genFailed = genJobs.filter((j) => j.status === 'failed');
  const mergeFailed = mergeJobs.filter((j) => j.status === 'failed');

  // Invoices that take part in Ký số and Gửi: 0-đồng ones are left out while `skipZero` is on.
  const targetIds = useMemo(
    () => (skipZero ? invoiceIds.filter((id) => !zeroIds.has(id)) : invoiceIds),
    [skipZero, invoiceIds, zeroIds],
  );
  const skippedZeroCount = invoiceIds.length - targetIds.length;

  const signJobsQuery = useSignJobs(targetIds, { poll: true, enabled: targetIds.length > 0 });
  // ĐNTT and BBĐS are tracked as two independent groups.
  const signGroups = useMemo(() => {
    const newestFirst = [...(signJobsQuery.data ?? [])].sort((x, y) => y.id - x.id);
    return SIGN_DOC_TYPES.map((dt) => buildSignGroup(dt, targetIds, newestFirst));
  }, [signJobsQuery.data, targetIds]);
  const signSettledCount = signGroups.reduce((n, g) => n + g.signed.size + g.failed.length, 0);

  const sendQuery = useLatestSendBatch(cohortKey, { poll: true });
  const batch = sendQuery.data ?? null;
  const sendProcessed = batch ? batch.sent + batch.failed + batch.skipped : 0;
  const sendAwaiting = (batch?.items ?? []).filter((it) => it.status === 'dispatching' || it.status === 'queued').length;

  // Refresh the cycle counts whenever a persisted item reaches a terminal state.
  const refreshCycle = () => queryClient.invalidateQueries({ queryKey: reconciliationKeys.cycle(cohortKey) });
  useOnIncrease(jobs.filter((j) => j.status !== 'processing').length, refreshCycle);
  useOnIncrease(signSettledCount, refreshCycle);
  useOnIncrease(sendProcessed, refreshCycle);

  if (cycleQuery.isLoading) return <TabLoading />;
  if (cycleQuery.isError || !data)
    return <TabError title="Failed to load cohort." onRetry={() => cycleQuery.refetch()} />;

  /* ── Stage statuses ─────────────────────────────────────────────────────── */
  const enqueueKind = enqueueJobs.isPending ? enqueueJobs.variables?.kind : undefined;
  const genStatus: StageStatus =
    genJobs.length > 0 || (enqueueKind && enqueueKind !== 'merge')
      ? jobsStageStatus(genJobs, genFailed.length, !!enqueueKind && enqueueKind !== 'merge')
      : data.pending_count === 0
        ? 'done'
        : 'idle';
  const mergeStatus: StageStatus =
    mergeJobs.length > 0 || enqueueKind === 'merge'
      ? jobsStageStatus(mergeJobs, mergeFailed.length, enqueueKind === 'merge')
      : mergedParents > 0
        ? 'done'
        : 'idle';
  const unapproved = new Set(unapprovedIds);
  // Confirm failures of the last request that still apply (the invoice is still waiting).
  const approveErrors = new Map(
    (approveInvoices.data ?? [])
      .filter((r) => r.status === 'failed' && unapproved.has(r.invoice_id))
      .map((r) => [r.invoice_id, APPROVE_REASON_LABEL[r.reason ?? ''] ?? r.reason ?? 'Xác nhận thất bại'] as const),
  );
  const approveStatus: StageStatus = approveInvoices.isPending
    ? 'running'
    : invoiceIds.length === 0
      ? 'idle'
      : unapprovedIds.length === 0
        ? 'done'
        : approveErrors.size > 0
          ? 'partial'
          : 'idle';
  const signStatus: StageStatus =
    enqueueSign.isPending || signGroups.some((g) => g.status === 'running')
      ? 'running'
      : signGroups.some((g) => g.status === 'partial')
        ? 'partial'
        : signGroups.every((g) => g.status === 'done')
          ? 'done'
          : 'idle';
  const sendBusy = sendEmail.isPending || retryFailedSend.isPending;
  const sendStatus: StageStatus =
    sendStageStatus(batch, sendBusy) ?? (data.mail_sent_count > 0 ? 'done' : 'idle');

  const stepStatuses: StageStatus[] = [genStatus, mergeStatus, approveStatus, signStatus, sendStatus];
  // A scheduled (not yet started) email batch shows as running but must not lock the other stages.
  // Waiting for delivery results does not lock the other stages; only handing emails over does.
  const running =
    [genStatus, mergeStatus, approveStatus, signStatus].includes('running') || sendBusy || isSendBatchDispatching(batch);
  const reached = stepStatuses.reduce((acc, s, i) => (s === 'done' ? i : acc), 0);
  const current = stepStatuses.findIndex((s) => s !== 'done');
  const stepperSteps = ['Sinh', 'Gộp', 'Xác nhận', 'Ký số', 'Gửi'].map((label, i) => ({
    label,
    description: STATUS_LABEL[stepStatuses[i]],
  }));

  /* ── Actions (all reached via ConfirmDialog) ────────────────────────────── */
  // One document's signing never waits on the other document; only the stages that
  // create / replace invoices lock it.
  const signLocked = [genStatus, mergeStatus, approveStatus].includes('running') || enqueueSign.isPending;
  // Confirming waits for the stages that create / replace invoices.
  const approveLocked = [genStatus, mergeStatus].includes('running') || approveInvoices.isPending;
  const invoiceLabel = (id: number) => invoiceCodes.get(id) ?? `HĐ #${id}`;
  // Who / what / how much each invoice is, so a sign row is recognisable at a glance.
  const servicesByInvoice = new Map<number, string[]>();
  for (const svc of data.services) {
    if (svc.invoice_id == null) continue;
    const names = servicesByInvoice.get(svc.invoice_id) ?? [];
    names.push(`${svc.service_name || 'Dịch vụ'} #${svc.service_id}`);
    servicesByInvoice.set(svc.invoice_id, names);
  }
  const invoiceInfo = new Map(
    data.invoices.map((inv) => {
      const names = servicesByInvoice.get(inv.invoice_id) ?? [];
      const services =
        names.length > 0
          ? names.join(', ')
          : inv.entity_service_name || ((inv.service_count ?? 1) > 1 ? `${inv.service_count} dịch vụ` : '');
      const merged = (inv.service_count ?? 1) > 1 ? `HĐ gộp ${inv.service_count} dịch vụ` : '';
      const customer = inv.company_name || inv.customer_name || '';
      return [inv.invoice_id, { sub: [customer, merged, services].filter(Boolean).join(' · '), total: formatMoney(inv.invoice_total) }] as const;
    }),
  );
  const canRetrySend = batch !== null && !isSendBatchActive(batch) && batch.failed > 0;

  function execute(c: ConfirmState) {
    switch (c.action) {
      case 'generate':
        enqueueJobs.mutate({ kind: 'generate', cohort_key: cohortKey, service_ids: pendingServiceIds });
        break;
      case 'merge':
        enqueueJobs.mutate({ kind: 'merge', cohort_key: cohortKey, invoice_ids: invoiceIds });
        break;
      case 'generate-one':
        enqueueJobs.mutate({ kind: 'generate', cohort_key: cohortKey, service_ids: [Number(c.service.service_id)] });
        break;
      case 'rollback':
      case 'regenerate':
        enqueueJobs.mutate({ kind: c.action, cohort_key: cohortKey, service_ids: [Number(c.service.service_id)] });
        break;
      case 'job-retry':
        retryJobs.mutate(c.ids);
        break;
      case 'approve':
        approveInvoices.mutate(c.ids, { onSettled: refreshCycle });
        break;
      case 'sign':
        enqueueSign.mutate({ invoice_ids: c.ids, document_types: [c.docType], skip_zero: skipZero });
        break;
      case 'resign':
        enqueueSign.mutate({ invoice_ids: [c.invoiceId], document_types: [c.docType], force: true, skip_zero: skipZero });
        break;
      case 'sign-job-retry':
        c.ids.forEach((id) => retrySignJob.mutate(id));
        break;
      case 'sign-job-cancel':
        cancelSignJob.mutate(c.job.id);
        break;
      case 'send':
        // The ConfirmDialog is the operator's explicit confirmation for a live send.
        sendEmail.mutate(
          { scope: 'pending', confirm: true, skip_zero: skipZero },
          { onSettled: () => queryClient.invalidateQueries({ queryKey: reconJobKeys.latestSend(cohortKey) }) },
        );
        break;
      case 'send-force':
        sendEmail.mutate(
          {
            scope: 'selected',
            invoice_ids: c.invoiceIds,
            confirm: true,
            skip_zero: skipZero,
            ...(c.mode === 'resend' ? { force_resend: true } : c.mode === 'no-mailer' ? { strict_mailer: false } : {}),
          },
          { onSettled: () => queryClient.invalidateQueries({ queryKey: reconJobKeys.latestSend(cohortKey) }) },
        );
        break;
      case 'send-retry':
        if (batch) retryFailedSend.mutate(batch.id);
        break;
    }
  }

  function confirmText(c: ConfirmState): string {
    switch (c.action) {
      case 'generate':
        return `Sinh hoá đơn statement cho ${pendingServiceIds.length} dịch vụ pending? Server xử lý bất đồng bộ — theo dõi từng dịch vụ bên dưới.`;
      case 'merge':
        return `Gộp ${invoiceIds.length} hoá đơn theo customer_mailers?`;
      case 'generate-one':
        return `Sinh hoá đơn statement cho dịch vụ ${c.service.service_name} #${c.service.service_id}?`;
      case 'rollback':
      case 'regenerate': {
        const what = c.action === 'regenerate' ? 'Sinh lại' : 'Hoàn tác';
        return `${what} hoá đơn đối soát của dịch vụ ${c.service.service_name} #${c.service.service_id}? Hoá đơn hiện tại bị huỷ, kỳ tính phí được khôi phục${c.action === 'regenerate' ? ', rồi sinh hoá đơn mới' : ''}. Nếu dịch vụ đã gộp: cả nhóm được tách ra (HĐ cha bị huỷ), sau đó cần Gộp lại. Chữ ký số cũ không còn hiệu lực — cần Ký lại. Bị chặn nếu đã gửi email cho khách.`;
      }
      case 'job-retry':
        return `Chạy lại ${c.ids.length} tác vụ lỗi từ bước đã hỏng?`;
      case 'approve':
        return c.ids.length === 1
          ? `Xác nhận hoá đơn ${invoiceLabel(c.ids[0])}? Hoá đơn chuyển sang "approved", ghi nhận doanh thu, có xuất hoá đơn, và được đồng bộ sang BI. Sau khi xác nhận mới ký số được.`
          : `Xác nhận ${c.ids.length} hoá đơn đang chờ? Mỗi hoá đơn chuyển sang "approved", ghi nhận doanh thu, có xuất hoá đơn, và được đồng bộ sang BI. Hãy soát số liệu trước — sau khi xác nhận mới ký số được.`;
      case 'sign':
        return c.ids.length === 1
          ? `Ký số ${DOC_LABEL[c.docType]} của hoá đơn ${invoiceLabel(c.ids[0])}? Tốn một lượt ký MISA.`
          : `Ký số ${c.ids.length} ${DOC_LABEL[c.docType]} chưa ký? Chỉ ký ${DOC_TITLE[c.docType]}; văn bản đã ký hoặc đang ký được bỏ qua. Mỗi văn bản tốn một lượt ký MISA.`;
      case 'resign':
        return `Ký lại ${DOC_LABEL[c.docType]} của hoá đơn ${invoiceLabel(c.invoiceId)}? Văn bản được kết xuất lại từ dữ liệu hiện tại và tốn thêm một lượt ký MISA. Bản đã ký hiện tại vẫn hiệu lực cho tới khi bản mới ký xong, sau đó chuyển thành lịch sử. Văn bản còn lại của hoá đơn không bị ảnh hưởng.`;
      case 'sign-job-cancel':
        return `Huỷ tiến trình ký ${DOC_LABEL[c.job.document_type]} của hoá đơn ${invoiceLabel(c.job.invoice_id)}? Tiến trình dừng hẳn và văn bản trở về "Chưa ký"; có thể ký lại sau.${c.job.step === 'signing' ? ' Văn bản đã được gửi sang MISA: nếu người ký vẫn duyệt bên MISA thì lượt ký đó bị bỏ, không được lưu.' : ''}`;
      case 'sign-job-retry':
        return `Thử lại ${c.ids.length} tác vụ ký lỗi? Mỗi tác vụ chạy lại từ bước đã hỏng.`;
      case 'send':
        return `Gửi email đối soát cho các hoá đơn chưa gửi của kỳ này, kèm bản BBĐS và ĐNTT đã ký? Hoá đơn còn văn bản chưa ký sẽ bị bỏ qua.${skippedZeroCount > 0 ? ` Bỏ qua ${skippedZeroCount} hoá đơn 0 đồng.` : ''}`;
      case 'send-force':
        if (c.mode === 'after-sign')
          return `Gửi email đối soát cho hoá đơn ${c.invoiceIds.map((id) => invoiceCodes.get(id) ?? id).join(', ')}, kèm BBĐS và ĐNTT đã ký? Hoá đơn vẫn bị bỏ qua nếu còn văn bản chưa ký.`;
        return c.mode === 'resend'
          ? `Gửi LẠI email đối soát cho hoá đơn ${c.invoiceIds.map((id) => invoiceCodes.get(id) ?? id).join(', ')}? Khách đã nhận email này trước đó.`
          : `Gửi email đối soát cho hoá đơn ${c.invoiceIds.map((id) => invoiceCodes.get(id) ?? id).join(', ')} tới email khách + email liên hệ (vì chưa có mailer)?`;
      case 'send-retry':
        return `Gửi lại ${batch?.failed ?? 0} email lỗi của đợt gửi #${batch?.id ?? ''}? Các mục đã gửi sẽ không bị gửi lại.`;
    }
  }

  /* ── Render helpers ─────────────────────────────────────────────────────── */
  const stageHeader = (title: string, hint: string, status: StageStatus, actions: ReactNode) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--vn-space-4)' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          <h3 style={{ margin: 0, fontSize: 'var(--vn-font-size-md)', fontWeight: 'var(--vn-font-weight-semibold)' }}>{title}</h3>
          <StatusPill tone={PILL_TONE[status]} loading={status === 'running'}>
            {STATUS_LABEL[status]}
          </StatusPill>
        </div>
        <p style={{ margin: 'var(--vn-space-1) 0 0', ...mutedSm }}>{hint}</p>
      </div>
      <div style={{ display: 'flex', gap: 'var(--vn-space-2)', flexShrink: 0 }}>{actions}</div>
    </div>
  );

  const errorAlert = (show: boolean, title: string, error: unknown) =>
    show ? (
      <div style={{ marginTop: 'var(--vn-space-2)' }}>
        <Alert tone="danger" title={title}>
          {apiErrorMessage(error)}
        </Alert>
      </div>
    ) : null;

  const loadError = (query: { isError: boolean; error: unknown; refetch: () => unknown }, what: string) =>
    query.isError ? (
      <div style={{ marginTop: 'var(--vn-space-2)' }}>
        <Alert tone="danger" title={`Không tải được ${what}`}>
          {apiErrorMessage(query.error)}{' '}
          <Button size="sm" variant="ghost" onClick={() => query.refetch()}>
            Tải lại
          </Button>
        </Alert>
      </div>
    ) : null;

  const jobProgress = (list: ReconJob[], status: StageStatus, unit: string, extra?: string) => {
    const terminal = list.filter((j) => j.status !== 'processing').length;
    const failed = list.filter((j) => j.status === 'failed').length;
    const autoRetrying = list.filter((j) => j.status === 'processing' && j.step_error).length;
    return (
      <Progress
        value={terminal}
        max={list.length || 1}
        tone={PROGRESS_TONE[status]}
        label={`${terminal}/${list.length} ${unit}${failed ? ` · ${failed} lỗi` : ''}${autoRetrying ? ` · ${autoRetrying} đang tự thử lại` : ''}${extra ? ` · ${extra}` : ''}`}
        showValue
      />
    );
  };

  const retryBtn = (onClick: () => void, label = 'Thử lại', disabled = false) => (
    <Button size="sm" variant="ghost" startIcon={<RotateCcw size={14} />} disabled={disabled} onClick={onClick}>
      {label}
    </Button>
  );
  const attemptsNote = (n?: number) =>
    typeof n === 'number' && n > 1 ? <span style={mutedXs}>lần {n}</span> : null;

  const jobItem = (j: ReconJob, title: string, sub?: string): ListItem => {
    const failed = j.status === 'failed';
    const step = jobStepLabel(j.step);
    return {
      key: `job-${j.id}`,
      title,
      sub,
      text: `${title} ${sub ?? ''}`,
      status: (
        <>
          {attemptsNote(j.attempts)}
          <StatusPill tone={reconJobTone(j)} loading={j.status === 'processing'}>
            {failed ? `Lỗi · ${step}` : j.step_error ? `Tự thử lại · ${step}` : step}
          </StatusPill>
        </>
      ),
      error: j.step_error,
      attention: failed || !!j.step_error,
      state: failed ? 'failed' : j.status === 'processing' ? 'processing' : 'done',
      action: failed ? retryBtn(() => setConfirm({ action: 'job-retry', ids: [j.id] }), 'Thử lại', retryJobs.isPending) : undefined,
    };
  };

  const genJobByService = new Map<number, ReconJob>();
  // Newest job per service across generate / regenerate / rollback.
  for (const j of [...genJobs].sort((a, b) => b.id - a.id)) {
    if (j.target_id != null && !genJobByService.has(j.target_id)) genJobByService.set(j.target_id, j);
  }
  const cohortServiceIds = new Set(data.services.map((svc) => Number(svc.service_id)));
  const orphanGenJobs = genJobs.filter((j) => j.target_id == null || !cohortServiceIds.has(j.target_id));

  // Sinh: every cohort service — its running/failed job if any, else issued
  // (invoice link + rollback) or pending (generate just this one).
  const genItems: ListItem[] = [
    ...data.services.map((svc): ListItem => {
      const title = `${svc.service_name || 'Dịch vụ'} #${svc.service_id}`;
      const customer = svc.company_name || svc.customer_name || '';
      const job = genJobByService.get(Number(svc.service_id));
      if (job && job.status !== 'done') return jobItem(job, title, customer);
      const issued = svc.status === 'issued';
      return {
        key: `svc-${svc.service_id}`,
        title,
        sub: customer,
        text: `${title} ${customer} ${svc.invoice_code ?? ''}`,
        status: (
          <>
            {issued && svc.invoice_id ? (
              <TableLink to={`/invoices/${svc.invoice_id}`}>{svc.invoice_code ?? `HĐ #${svc.invoice_id}`}</TableLink>
            ) : null}
            <StatusPill tone={issued ? 'success' : 'neutral'}>{issued ? 'Đã sinh' : 'Chờ sinh · chưa chạy'}</StatusPill>
          </>
        ),
        attention: !issued,
        state: issued ? 'done' : 'pending',
        action: issued ? (
          <span style={{ display: 'inline-flex', gap: 'var(--vn-space-1)' }}>
            <Button size="sm" variant="ghost" disabled={running || enqueueJobs.isPending} onClick={() => setConfirm({ action: 'regenerate', service: svc })}>
              Sinh lại
            </Button>
            <Button size="sm" variant="ghost" disabled={running || enqueueJobs.isPending} onClick={() => setConfirm({ action: 'rollback', service: svc })}>
              Hoàn tác
            </Button>
          </span>
        ) : (
          <Button size="sm" variant="ghost" disabled={running || enqueueJobs.isPending} onClick={() => setConfirm({ action: 'generate-one', service: svc })}>
            Sinh
          </Button>
        ),
      };
    }),
    ...orphanGenJobs.map((j) => jobItem(j, `Dịch vụ #${j.target_id ?? j.id}`)),
  ];

  const retryAllButton = (failed: ReconJob[]) =>
    failed.length > 0 ? (
      <Button
        size="sm"
        variant="secondary"
        disabled={retryJobs.isPending}
        onClick={() => setConfirm({ action: 'job-retry', ids: failed.map((j) => j.id) })}
      >
        Thử lại tất cả lỗi ({failed.length})
      </Button>
    ) : null;

  // Xác nhận: every cohort invoice with its own status; waiting ones can be confirmed.
  const approveItems: ListItem[] = data.invoices.map((inv): ListItem => {
    const id = inv.invoice_id;
    const info = invoiceInfo.get(id);
    const waiting = unapproved.has(id);
    const error = approveErrors.get(id);
    return {
      key: `approve-${id}`,
      title: <TableLink to={`/invoices/${id}`}>{inv.invoice_code}</TableLink>,
      sub: info?.sub,
      text: `${inv.invoice_code} ${info?.sub ?? ''}`,
      amount: info?.total,
      status: (
        <>
          <StatusPill tone={error ? 'danger' : waiting ? 'neutral' : 'success'}>
            {error ? 'Xác nhận lỗi' : waiting ? 'Chờ xác nhận' : 'Đã xác nhận'}
          </StatusPill>
        </>
      ),
      error,
      attention: waiting,
      state: error ? 'failed' : waiting ? 'waiting' : 'approved',
      action: waiting ? (
        <Button size="sm" variant="ghost" disabled={approveLocked} onClick={() => setConfirm({ action: 'approve', ids: [id] })}>
          Xác nhận
        </Button>
      ) : undefined,
    };
  });

  // Ký: one row per invoice inside each document group, so ĐNTT and BBĐS are
  // signed, retried, re-signed and opened independently.
  const signGroupItems = (g: SignGroup): ListItem[] =>
    targetIds.map((id): ListItem => {
      const code = invoiceLabel(id);
      const j = g.latest.get(id);
      const signedJob = g.signed.get(id);
      const failed = !!j && isSignFailed(j);
      const info = invoiceInfo.get(id);
      const waitingApproval = unapproved.has(id);
      return {
        key: `${g.docType}-${id}`,
        title: <TableLink to={`/invoices/${id}`}>{code}</TableLink>,
        text: `${code} ${info?.sub ?? ''}`,
        sub: info?.sub,
        amount: info?.total,
        status: (
          <>
            {signedJob && j && j.id !== signedJob.id && <span style={mutedXs}>bản ký trước vẫn hiệu lực</span>}
            {attemptsNote(j?.attempts)}
            {j ? (
              <StatusPill tone={signJobTone(j)} loading={j.status === 'processing'}>
                {signJobLabel(j)}
              </StatusPill>
            ) : (
              <StatusPill tone="neutral">{waitingApproval ? 'Chưa ký · chờ xác nhận hoá đơn ở bước 3' : 'Chưa ký · chưa đưa vào hàng đợi'}</StatusPill>
            )}
          </>
        ),
        error: j?.step_error,
        // Anything not signed yet still needs the operator, not only failures.
        attention: !j || failed || !!j.step_error,
        state: !j ? 'unsigned' : failed ? 'failed' : j.status === 'processing' ? 'processing' : 'signed',
        action: (
          <span style={{ display: 'inline-flex', gap: 'var(--vn-space-1)' }}>
            {signedJob && (
              <>
                <Button size="sm" variant="ghost" disabled={openSignedFile.isPending} onClick={() => openSignedFile.mutate({ id: signedJob.id, mode: 'view' })}>
                  Xem
                </Button>
                <Button size="sm" variant="ghost" disabled={openSignedFile.isPending} onClick={() => openSignedFile.mutate({ id: signedJob.id, mode: 'download' })}>
                  Tải
                </Button>
              </>
            )}
            {!j && (
              <Button size="sm" variant="ghost" disabled={signLocked || waitingApproval} onClick={() => setConfirm({ action: 'sign', docType: g.docType, ids: [id] })}>
                Ký
              </Button>
            )}
            {j && failed && retryBtn(() => setConfirm({ action: 'sign-job-retry', ids: [j.id] }), 'Thử lại', retrySignJob.isPending)}
            {j && (failed || (j.status === 'processing' && j.step !== 'storing')) && (
              <Button size="sm" variant="ghost" disabled={cancelSignJob.isPending} onClick={() => setConfirm({ action: 'sign-job-cancel', job: j })}>
                Huỷ
              </Button>
            )}
            {j?.status === 'done' && (
              <Button size="sm" variant="ghost" disabled={signLocked || waitingApproval} onClick={() => setConfirm({ action: 'resign', docType: g.docType, invoiceId: id })}>
                Ký lại
              </Button>
            )}
          </span>
        ),
      };
    });

  const signGroupSection = (g: SignGroup) => {
    const label = DOC_LABEL[g.docType];
    const total = targetIds.length;
    const signedCount = targetIds.filter((id) => g.signed.has(id)).length;
    // Only confirmed invoices can be put in the sign queue.
    const signableIds = g.unsignedIds.filter((id) => !unapproved.has(id));
    const awaitingApproval = g.unsignedIds.length - signableIds.length;
    return (
      <div key={g.docType} style={{ marginTop: 'var(--vn-space-4)' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--vn-space-2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            <h4 style={{ margin: 0, fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-semibold)' }}>{DOC_TITLE[g.docType]}</h4>
            <StatusPill tone={PILL_TONE[g.status]} loading={g.status === 'running'}>
              {g.processing > 0 ? `Đang ký ${g.processing}` : `Đã ký ${signedCount}/${total}`}
            </StatusPill>
          </div>
          <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
            {g.failed.length > 0 && (
              <Button
                size="sm"
                variant="secondary"
                disabled={retrySignJob.isPending}
                onClick={() => setConfirm({ action: 'sign-job-retry', ids: g.failed.map((j) => j.id) })}
              >
                Thử lại {g.failed.length} lỗi
              </Button>
            )}
            <Button
              size="sm"
              variant="primary"
              disabled={signableIds.length === 0 || signLocked}
              onClick={() => setConfirm({ action: 'sign', docType: g.docType, ids: signableIds })}
            >
              {signedCount === total
                ? 'Đã ký đủ'
                : signableIds.length === 0 && awaitingApproval > 0
                  ? `${awaitingApproval} ${label} chờ xác nhận hoá đơn`
                  : `Ký ${signableIds.length} ${label} chưa ký`}
            </Button>
          </div>
        </div>
        <div style={{ marginTop: 'var(--vn-space-2)' }}>
          <Progress
            value={signedCount}
            max={total || 1}
            tone={PROGRESS_TONE[g.status]}
            label={`Đã ký ${signedCount} · đang ký ${g.processing} · lỗi ${g.failed.length} · chưa ký ${g.unsignedIds.length} (không bị đụng tới)${awaitingApproval ? ` · trong đó ${awaitingApproval} chờ xác nhận hoá đơn` : ''} · tổng ${total} ${label}`}
            showValue
          />
        </div>
        <ItemList items={signGroupItems(g)} unit={label} states={SIGN_STATES} headings={{ title: 'Hoá đơn', sub: 'Khách hàng · dịch vụ' }} />
      </div>
    );
  };
  // Gửi: every cohort invoice — its line in the latest batch if any, else whether the
  // customer was already mailed. Each one can be test-sent to the operator's address.
  const testTo = testEmail.trim();
  const testReady = /^\S+@\S+\.\S+$/.test(testTo);
  const sendTest = () => {
    if (testInvoiceId === null || !testReady) return;
    sendEmail.mutate(
      // strict_mailer off: a test goes to the operator, so a missing customer mailer must not block it.
      { scope: 'selected', invoice_ids: [testInvoiceId], test_recipient: testTo, strict_mailer: false, skip_zero: skipZero },
      { onSettled: () => queryClient.invalidateQueries({ queryKey: reconJobKeys.latestSend(cohortKey) }) },
    );
    setTestInvoiceId(null);
  };
  const sendLocked = sendBusy || isSendBatchActive(batch) || [genStatus, mergeStatus].includes('running');
  const batchItemByInvoice = new Map((batch?.items ?? []).map((it) => [it.invoice_id, it]));
  const sendItems: ListItem[] = data.invoices.filter((inv) => !skipZero || !zeroIds.has(inv.invoice_id)).map((inv): ListItem => {
    const id = inv.invoice_id;
    const it = batchItemByInvoice.get(id);
    const info = invoiceInfo.get(id);
    const mailed = inv.invoice_mail_count > 0;
    const problem = !!it && isSendProblem(it);
    const sending = !!it && isSendInFlight(it.status) && isSendBatchActive(batch);
    const noMailer = it?.status === 'skipped' && it.reason === 'no-sendable-mailer' && !batch?.test_recipient;
    // Signed state of each document, from the Ký stage: what this email will carry.
    const docPills = [...signGroups].reverse().map((g) => {
      const signed = g.signed.has(id);
      const j = g.latest.get(id);
      const text = signed ? 'đã ký' : j?.status === 'processing' ? 'đang ký' : j && isSignFailed(j) ? 'ký lỗi' : 'chưa ký';
      return (
        <StatusPill key={g.docType} tone={signed ? 'success' : j && isSignFailed(j) ? 'danger' : 'neutral'} loading={!signed && j?.status === 'processing'}>
          {`${DOC_LABEL[g.docType]} ${text}`}
        </StatusPill>
      );
    });
    return {
      key: `send-${id}`,
      extra: <span style={{ display: 'inline-flex', gap: 'var(--vn-space-2)' }}>{docPills}</span>,
      title: <TableLink to={`/invoices/${id}`}>{inv.invoice_code}</TableLink>,
      sub: [info?.sub, it?.recipients].filter(Boolean).join(' · '),
      text: `${inv.invoice_code} ${info?.sub ?? ''} ${it?.recipients ?? ''}`,
      amount: info?.total,
      status: (
        <>
          {it ? (
            <StatusPill tone={SEND_ITEM_TONE[it.status] ?? 'neutral'} loading={sending}>
              {`${batch?.test_recipient ? 'Gửi thử · ' : ''}${SEND_ITEM_LABEL[it.status] ?? it.status}`}
            </StatusPill>
          ) : (
            <StatusPill tone={mailed ? 'success' : 'neutral'}>{mailed ? 'Đã gửi' : 'Chưa gửi · chưa đưa vào đợt gửi'}</StatusPill>
          )}
        </>
      ),
      error: it?.status === 'failed' ? sendReason(it.error) || sendReason(it.reason) : problem ? sendReason(it?.reason) : null,
      attention: problem || !mailed,
      state: sending ? 'sending' : it?.status === 'failed' ? 'failed' : problem ? 'skipped' : mailed || (it?.status === 'success' && !batch?.test_recipient) ? 'sent' : 'unsent',
      action: (
        <span style={{ display: 'inline-flex', gap: 'var(--vn-space-1)' }}>
          <Button
            size="sm"
            variant="ghost"
            disabled={sendLocked}
            onClick={() => setTestInvoiceId(id)}
          >
            Gửi thử
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={sendLocked}
            onClick={() => setConfirm({ action: 'send-force', invoiceIds: [id], mode: noMailer ? 'no-mailer' : mailed ? 'resend' : 'after-sign' })}
          >
            {noMailer ? 'Gửi tới email khách' : mailed ? 'Gửi lại' : 'Gửi'}
          </Button>
        </span>
      ),
    };
  });
  const mergedCount = mergeJobs[0]?.result?.merged_invoice_ids?.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--vn-space-4)', minWidth: 0 }}>
      <Card>
        <CardBody>
          <Stepper steps={stepperSteps} current={current < 0 ? 4 : current} reached={reached} />
          <label
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', marginTop: 'var(--vn-space-3)', cursor: 'pointer', ...mutedSm }}
          >
            <Switch checked={skipZero} onCheckedChange={setSkipZero} />
            <span>
              Bỏ qua khách hàng 0 đồng khi ký BBĐS, ĐNTT và gửi email
              {zeroIds.size > 0 ? ` (${zeroIds.size} hoá đơn 0 đồng${skipZero ? ' đang ẩn' : ''})` : ''}
            </span>
          </label>
        </CardBody>
      </Card>

      {/* ── 1. Sinh — one persisted job per pending service ── */}
      <Card>
        <CardBody>
          {stageHeader(
            '1. Sinh hoá đơn',
            'Sinh statement cho dịch vụ pending — server xử lý từng dịch vụ, tự thử lại lỗi tạm thời',
            genStatus,
            <>
              {retryAllButton(genFailed)}
              <Button
                size="sm"
                variant="primary"
                disabled={pendingServiceIds.length === 0 || running}
                onClick={() => setConfirm({ action: 'generate' })}
              >
                {genStatus === 'running' ? <Spinner size="sm" /> : `Sinh ${pendingServiceIds.length} dịch vụ`}
              </Button>
            </>,
          )}
          <div style={{ marginTop: 'var(--vn-space-3)' }}>
            {genJobs.length > 0 ? (
              jobProgress(genJobs, genStatus, 'dịch vụ')
            ) : (
              <span style={mutedSm}>
                {data.issued_count}/{data.issued_count + data.pending_count} dịch vụ đã sinh
              </span>
            )}
          </div>
          {errorAlert(
            enqueueJobs.isError && enqueueJobs.variables?.kind !== 'merge',
            'Không đưa được vào hàng đợi sinh',
            enqueueJobs.error,
          )}
          {errorAlert(retryJobs.isError, 'Không chạy lại được tác vụ', retryJobs.error)}
          {loadError(jobsQuery, 'tác vụ sinh/gộp')}
          <div style={{ marginTop: 'var(--vn-space-2)', ...mutedSm }}>{stateSummary(genItems, GEN_STATES)}</div>
          <ItemList items={genItems} unit="dịch vụ" states={GEN_STATES} headings={{ title: 'Dịch vụ', sub: 'Khách hàng' }} />
        </CardBody>
      </Card>

      {/* ── 2. Gộp — one persisted job for the cohort's invoices ── */}
      <Card>
        <CardBody>
          {stageHeader(
            '2. Gộp hoá đơn',
            'Gộp theo customer_mailers',
            mergeStatus,
            <>
              {retryAllButton(mergeFailed)}
              <Button
                size="sm"
                variant="primary"
                disabled={invoiceIds.length === 0 || running}
                onClick={() => setConfirm({ action: 'merge' })}
              >
                {mergeStatus === 'running' ? <Spinner size="sm" /> : 'Gộp hoá đơn'}
              </Button>
            </>,
          )}
          <div style={{ marginTop: 'var(--vn-space-3)' }}>
            {mergeJobs.length > 0 ? (
              jobProgress(mergeJobs, mergeStatus, 'tác vụ gộp', mergedCount !== undefined ? `${mergedCount} HĐ cha` : undefined)
            ) : (
              <span style={mutedSm}>
                {mergedParents > 0 ? `${mergedParents} HĐ cha` : `${invoiceIds.length} hoá đơn`}
                {mergeSuperseded && ' · có nhóm đã tách do Sinh lại / Hoàn tác — cần Gộp lại'}
              </span>
            )}
          </div>
          {errorAlert(
            enqueueJobs.isError && enqueueJobs.variables?.kind === 'merge',
            'Không đưa được vào hàng đợi gộp',
            enqueueJobs.error,
          )}
          <ItemList items={mergeJobs.map((j) => jobItem(j, mergeJobTitle(j)))} unit="tác vụ gộp" states={MERGE_STATES} headings={{ title: 'Tác vụ gộp' }} />
        </CardBody>
      </Card>

      {/* ── 3. Xác nhận — invoices must be confirmed before they can be signed ── */}
      <Card>
        <CardBody>
          {stageHeader(
            '3. Xác nhận hoá đơn',
            'Soát số liệu rồi xác nhận hoá đơn (waiting-confirm → approved, có xuất hoá đơn). Hoá đơn chưa xác nhận không ký số được',
            approveStatus,
            <Button
              size="sm"
              variant="primary"
              disabled={unapprovedIds.length === 0 || approveLocked}
              onClick={() => setConfirm({ action: 'approve', ids: unapprovedIds })}
            >
              {approveStatus === 'running' ? (
                <Spinner size="sm" />
              ) : unapprovedIds.length === 0 && invoiceIds.length > 0 ? (
                'Đã xác nhận đủ'
              ) : (
                `Xác nhận ${unapprovedIds.length} hoá đơn`
              )}
            </Button>,
          )}
          <div style={{ marginTop: 'var(--vn-space-3)' }}>
            <Progress
              value={invoiceIds.length - unapprovedIds.length}
              max={invoiceIds.length || 1}
              tone={PROGRESS_TONE[approveStatus]}
              label={`${invoiceIds.length - unapprovedIds.length}/${invoiceIds.length} hoá đơn đã xác nhận${approveErrors.size ? ` · ${approveErrors.size} lỗi` : ''}`}
              showValue
            />
          </div>
          {errorAlert(approveInvoices.isError, 'Không xác nhận được hoá đơn', approveInvoices.error)}
          {invoiceIds.length > 0 ? (
            <ItemList items={approveItems} unit="hoá đơn" states={APPROVE_STATES} headings={{ title: 'Hoá đơn', sub: 'Khách hàng · dịch vụ' }} />
          ) : (
            <div style={{ marginTop: 'var(--vn-space-3)', ...mutedSm }}>Chưa có hoá đơn để xác nhận.</div>
          )}
        </CardBody>
      </Card>

      {/* ── 4. Ký số — ĐNTT and BBĐS as two separately managed document groups ── */}
      <Card>
        <CardBody>
          {stageHeader(
            '4. Ký số',
            'ĐNTT và BBĐS là hai văn bản riêng — ký, thử lại, ký lại và xem bản đã ký theo từng văn bản của từng hoá đơn. Chỉ ký được hoá đơn đã xác nhận ở bước 3',
            signStatus,
            null,
          )}

          {errorAlert(enqueueSign.isError, 'Không đưa được vào hàng đợi ký', enqueueSign.error)}
          {errorAlert(retrySignJob.isError, 'Không thử lại được tác vụ ký', retrySignJob.error)}
          {errorAlert(cancelSignJob.isError, 'Không huỷ được tiến trình ký (văn bản đã ký xong thì không huỷ được)', cancelSignJob.error)}
          {errorAlert(openSignedFile.isError, 'Không mở được bản đã ký', openSignedFile.error)}
          {loadError(signJobsQuery, 'tác vụ ký')}

          {targetIds.length > 0 ? (
            signGroups.map(signGroupSection)
          ) : (
            <div style={{ marginTop: 'var(--vn-space-3)', ...mutedSm }}>Chưa có hoá đơn để ký.</div>
          )}
        </CardBody>
      </Card>

      {/* ── 5. Gửi — latest persisted email batch with per-item status ── */}
      <Card>
        <CardBody>
          {stageHeader(
            '5. Gửi email đối soát',
            'Gửi email đối soát kèm BBĐS và ĐNTT đã ký — hoá đơn chưa ký đủ bị bỏ qua. "Đã gửi" là kết quả gửi thật từ automation; lỗi gửi hiện ngay dưới hoá đơn',
            sendStatus,
            <>
              {canRetrySend && (
                <Button size="sm" variant="secondary" disabled={sendBusy} onClick={() => setConfirm({ action: 'send-retry' })}>
                  Gửi lại các mục lỗi ({batch.failed})
                </Button>
              )}
              <Button
                size="sm"
                variant="primary"
                disabled={targetIds.length === 0 || running}
                onClick={() => setConfirm({ action: 'send' })}
              >
                {sendStatus === 'running' ? <Spinner size="sm" /> : 'Gửi email'}
              </Button>
            </>,
          )}

          <div style={{ marginTop: 'var(--vn-space-3)' }}>
            {batch ? (
              <Progress
                value={sendProcessed}
                max={batch.total || 1}
                tone={PROGRESS_TONE[sendStatus]}
                label={`Đợt ${batch.test_recipient ? `gửi thử tới ${batch.test_recipient} ` : ''}#${batch.id}: ${sendProcessed}/${batch.total} · ${batch.sent} đã gửi${batch.failed ? ` · ${batch.failed} lỗi` : ''}${batch.skipped ? ` · ${batch.skipped} bỏ qua` : ''}${sendAwaiting ? ` · ${sendAwaiting} chờ automation gửi` : ''}${batch.status === 'scheduled' ? ' · đã lên lịch' : ''}`}
                showValue
              />
            ) : (
              <span style={mutedSm}>{data.mail_sent_count} email đã gửi</span>
            )}
          </div>

          {errorAlert(sendEmail.isError, 'Không gửi được email', sendEmail.error)}
          {errorAlert(retryFailedSend.isError, 'Không gửi lại được các mục lỗi', retryFailedSend.error)}
          {loadError(sendQuery, 'trạng thái gửi')}

          <div style={{ marginTop: 'var(--vn-space-2)', ...mutedSm }}>
            {stateSummary(sendItems, SEND_STATES)} · Đủ bản ký (BBĐS + ĐNTT) {targetIds.filter((id) => signGroups.every((g) => g.signed.has(id))).length}/{targetIds.length}
          </div>
          <ItemList items={sendItems} unit="hoá đơn" states={SEND_STATES} headings={{ title: 'Hoá đơn', sub: 'Khách hàng · dịch vụ · người nhận', extra: 'Bản ký' }} />
        </CardBody>
      </Card>

      {/* Gửi thử — the dialog is the confirmation: pick the address, then send. */}
      <Dialog open={testInvoiceId !== null} onOpenChange={(o) => !o && setTestInvoiceId(null)}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Gửi thử email đối soát{testInvoiceId !== null ? ` · ${invoiceLabel(testInvoiceId)}` : ''}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                sendTest();
              }}
            >
              <Field
                label="Email nhận thử"
                description="Khách hàng không nhận email này và hoá đơn không bị tính là đã gửi. Email kèm các bản BBĐS / ĐNTT đã ký tới thời điểm này."
              >
                {() => (
                  <Input
                    type="email"
                    autoFocus
                    placeholder="ten@vnetwork.vn"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                  />
                )}
              </Field>
            </form>
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="secondary">Huỷ</Button>
            </DialogClose>
            <Button variant="primary" disabled={!testReady || sendLocked} onClick={sendTest}>
              Gửi thử
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirm !== null}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm?.action === 'sign-job-cancel' ? 'Huỷ tiến trình ký' : confirm && RETRY_ACTIONS.includes(confirm.action) ? 'Chạy lại các mục lỗi' : 'Xác nhận chạy'}
        description={confirm ? confirmText(confirm) : ''}
        confirmLabel={confirm?.action === 'sign-job-cancel' ? 'Huỷ tiến trình' : 'Chạy'}
        onConfirm={() => {
          if (confirm) execute(confirm);
          setConfirm(null);
        }}
      />
    </div>
  );
}
